import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

interface RightRailOverlayContextProps {
  closeOverlay: () => void
  isOverlayOpen: boolean
  openOverlay: (content: React.ReactNode) => void
  overlayContent: React.ReactNode
}

const RightRailOverlayContext = createContext<
  RightRailOverlayContextProps | undefined
>(undefined)

interface RightRailOverlayProviderProps {
  children: React.ReactNode
}

const RightRailOverlayProvider = ({
  children,
}: RightRailOverlayProviderProps) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  const [overlayContent, setOverlayContent] = useState<React.ReactNode>(null)

  const closeOverlay = useCallback(() => {
    setOverlayContent(null)
    setIsOverlayOpen(false)
  }, [])

  const openOverlay = useCallback((content: React.ReactNode) => {
    setOverlayContent(content)
    setIsOverlayOpen(true)
  }, [])

  const contextValue = useMemo(
    () => ({
      closeOverlay,
      isOverlayOpen,
      openOverlay,
      overlayContent,
    }),
    [closeOverlay, isOverlayOpen, openOverlay, overlayContent],
  )

  return (
    <RightRailOverlayContext.Provider value={contextValue}>
      {children}
    </RightRailOverlayContext.Provider>
  )
}

export const useRightRailOverlay = (): RightRailOverlayContextProps => {
  const context = useContext(RightRailOverlayContext)
  if (context === undefined) {
    throw new Error(
      'useRightRailOverlay must be used within a RightRailOverlayProvider',
    )
  }
  return context
}

export { RightRailOverlayContext, RightRailOverlayProvider }
